/**
 * NexusPlatformView — Platform-level cross-org Nexus routing
 * Phase 2+3: De-identified referrals, accept/decline workflow, AI smart matching
 * Only visible to super_admin and platform_admin roles
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Globe, Building2, Users, Send, RefreshCw, Search,
  ArrowRight, ChevronDown, ChevronUp, MapPin, Clock,
  Shield, Sparkles, CheckCircle, XCircle, Brain, Lock
} from "lucide-react";
import { toast } from "sonner";

const NexusPlatformView = ({ token, API, user }) => {
  const [view, setView] = useState('overview');
  const [capacity, setCapacity] = useState(null);
  const [referrals, setReferrals] = useState([]);
  const [crossOrgReferrals, setCrossOrgReferrals] = useState([]);
  const [clinicians, setClinicians] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [searchCounty, setSearchCounty] = useState('');
  const [searchSpec, setSearchSpec] = useState('');
  const [expandedOrg, setExpandedOrg] = useState(null);
  const [aiMatchResult, setAiMatchResult] = useState(null);
  const [matchingRefId, setMatchingRefId] = useState(null);
  const [declineReason, setDeclineReason] = useState('');
  const [decliningRefId, setDecliningRefId] = useState(null);

  // De-identified referral form
  const [newRef, setNewRef] = useState({
    age_group: '', gender: '', county: '', service_type: '', urgency: 'routine',
    risk_level: 'low', presenting_need: '', diagnosis_codes: '',
    language_preference: 'English', preferred_modality: 'any', payer_type: 'medicaid',
    clinical_notes: '', client_first_name: '', client_last_name: '',
    client_dob: '', medicaid_id: '',
  });

  const fetchCapacity = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/nexus/platform/capacity`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) setCapacity(data.data);
    } catch (e) { console.error(e); }
  }, [API, token]);

  const fetchReferrals = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/nexus/platform/referrals`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) setReferrals(data.data);
    } catch (e) { console.error(e); }
  }, [API, token]);

  const fetchCrossOrgReferrals = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/nexus/platform/cross-org-referrals`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) setCrossOrgReferrals(data.data);
    } catch (e) { console.error(e); }
  }, [API, token]);

  const fetchAnalytics = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/nexus/platform/analytics`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) setAnalytics(data.data);
    } catch (e) { console.error(e); }
  }, [API, token]);

  const searchClinicians = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (searchCounty) params.set('county', searchCounty);
      if (searchSpec) params.set('specialization', searchSpec);
      const res = await fetch(`${API}/api/nexus/platform/clinicians?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) setClinicians(data.data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [API, token, searchCounty, searchSpec]);

  useEffect(() => {
    const loadAll = async () => {
      setInitialLoading(true);
      await Promise.all([fetchCapacity(), fetchReferrals(), fetchCrossOrgReferrals(), fetchAnalytics()]);
      setInitialLoading(false);
    };
    loadAll();
  }, [fetchCapacity, fetchReferrals, fetchCrossOrgReferrals, fetchAnalytics]);

  // Create de-identified cross-org referral
  const createDeidentifiedReferral = async () => {
    try {
      setLoading(true);
      const payload = {
        ...newRef,
        diagnosis_codes: newRef.diagnosis_codes ? newRef.diagnosis_codes.split(',').map(s => s.trim()).filter(Boolean) : [],
      };
      const res = await fetch(`${API}/api/nexus/platform/referrals/de-identified`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (data.success) {
        toast.success('De-identified referral created');
        setView('cross-org');
        setNewRef({ age_group: '', gender: '', county: '', service_type: '', urgency: 'routine',
          risk_level: 'low', presenting_need: '', diagnosis_codes: '', language_preference: 'English',
          preferred_modality: 'any', payer_type: 'medicaid', clinical_notes: '',
          client_first_name: '', client_last_name: '', client_dob: '', medicaid_id: '' });
        fetchCrossOrgReferrals();
      } else {
        toast.error(data.detail || 'Error creating referral');
      }
    } catch (e) { toast.error('Error creating referral'); }
    finally { setLoading(false); }
  };

  // Run AI cross-org matching
  const runAiMatch = async (referralId) => {
    try {
      setMatchingRefId(referralId);
      const res = await fetch(`${API}/api/nexus/platform/cross-org-referrals/${referralId}/ai-match`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) {
        setAiMatchResult({ referralId, ...data.data });
        toast.success(`AI found ${data.data.matches?.length || 0} matches across ${data.data.orgs_searched} orgs`);
      }
    } catch (e) { toast.error('Error running AI match'); }
    finally { setMatchingRefId(null); }
  };

  // Route referral to org
  const routeReferral = async (referralId, orgId, clinicianId) => {
    try {
      const res = await fetch(`${API}/api/nexus/platform/cross-org-referrals/${referralId}/route`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ organization_id: orgId, clinician_id: clinicianId }),
      });
      const data = await res.json();
      if (data.success) {
        toast.success(data.message);
        setAiMatchResult(null);
        fetchCrossOrgReferrals();
      }
    } catch (e) { toast.error('Error routing referral'); }
  };

  // Accept referral (org admin)
  const acceptReferral = async (referralId) => {
    try {
      const res = await fetch(`${API}/api/nexus/platform/cross-org-referrals/${referralId}/accept`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) {
        toast.success(data.message);
        fetchCrossOrgReferrals();
      }
    } catch (e) { toast.error('Error accepting referral'); }
  };

  // Decline referral (org admin)
  const declineReferral = async (referralId) => {
    try {
      const res = await fetch(`${API}/api/nexus/platform/cross-org-referrals/${referralId}/decline`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ reason: declineReason }),
      });
      const data = await res.json();
      if (data.success) {
        toast.success(data.message);
        setDecliningRefId(null);
        setDeclineReason('');
        fetchCrossOrgReferrals();
      }
    } catch (e) { toast.error('Error declining referral'); }
  };

  const statusColor = (s) => {
    const map = {
      intake: 'bg-blue-100 text-blue-800', matching: 'bg-purple-100 text-purple-800',
      pending_acceptance: 'bg-amber-100 text-amber-800', accepted: 'bg-green-100 text-green-800',
      declined: 'bg-red-100 text-red-800', routed: 'bg-indigo-100 text-indigo-800',
      placed: 'bg-emerald-100 text-emerald-800', closed: 'bg-gray-100 text-gray-600',
      triaged: 'bg-amber-100 text-amber-800',
    };
    return map[s] || 'bg-gray-100 text-gray-600';
  };

  return (
    <div className="space-y-5" data-testid="nexus-platform-view">
      {/* Loading State */}
      {initialLoading && (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="w-6 h-6 text-indigo-500 animate-spin mr-3" />
          <span className="text-sm text-slate-500">Loading Nexus Network data...</span>
        </div>
      )}

      {!initialLoading && (
        <>
          {/* Summary Cards — always visible */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <Card className="border-slate-200">
              <CardContent className="p-4">
                <div className="text-xs text-slate-500 mb-1">Organizations</div>
                <div className="text-2xl font-bold text-slate-900">{capacity?.summary?.total_organizations || 0}</div>
              </CardContent>
            </Card>
            <Card className="border-slate-200">
              <CardContent className="p-4">
                <div className="text-xs text-slate-500 mb-1">Total Clinicians</div>
                <div className="text-2xl font-bold text-slate-900">{capacity?.summary?.total_clinicians || 0}</div>
                <div className="text-xs text-green-600">{capacity?.summary?.total_accepting || 0} accepting</div>
              </CardContent>
            </Card>
            <Card className="border-slate-200">
              <CardContent className="p-4">
                <div className="text-xs text-slate-500 mb-1">Network Utilization</div>
                <div className="text-2xl font-bold text-slate-900">{capacity?.summary?.network_utilization_pct || 0}%</div>
              </CardContent>
            </Card>
            <Card className="border-slate-200">
              <CardContent className="p-4">
                <div className="text-xs text-slate-500 mb-1">Cross-Org Referrals</div>
                <div className="text-2xl font-bold text-slate-900">{crossOrgReferrals.length}</div>
                <div className="text-xs text-amber-600">{crossOrgReferrals.filter(r => r.status === 'pending_acceptance').length} pending</div>
              </CardContent>
            </Card>
            <Card className="border-slate-200">
              <CardContent className="p-4">
                <div className="text-xs text-slate-500 mb-1 flex items-center gap-1"><Shield className="w-3 h-3" /> PHI Status</div>
                <div className="text-sm font-semibold text-green-700">HIPAA Compliant</div>
                <div className="text-xs text-slate-400">De-identified routing</div>
              </CardContent>
            </Card>
          </div>

      {/* Sub-nav */}
      <div className="flex gap-2 flex-wrap">
        {[
          { key: 'overview', icon: Building2, label: 'Overview' },
          { key: 'cross-org', icon: Globe, label: 'Cross-Org Referrals' },
          { key: 'new-referral', icon: Send, label: '+ New Referral' },
          { key: 'search', icon: Search, label: 'Search' },
          { key: 'referrals', icon: Clock, label: 'Legacy Referrals' },
        ].map(({ key, icon: Icon, label }) => (
          <Button
            key={key} size="sm"
            variant={view === key ? 'default' : 'outline'}
            className={`text-xs ${view === key ? 'bg-indigo-600 text-white' : ''}`}
            onClick={() => setView(key)}
            data-testid={`platform-view-${key}`}
          >
            <Icon className="w-3 h-3 mr-1" />{label}
          </Button>
        ))}
      </div>

      {/* Overview: Org Capacity Cards */}
      {view === 'overview' && (
        <div className="space-y-3">
          {(!capacity || !capacity.organizations || capacity.organizations.length === 0) ? (
            <Card className="border-slate-200">
              <CardContent className="p-8 text-center">
                <Building2 className="w-8 h-8 mx-auto text-slate-300 mb-3" />
                <p className="text-sm text-slate-500">No organization capacity data available yet.</p>
                <p className="text-xs text-slate-400 mt-1">Clinicians need Nexus profiles to appear here.</p>
              </CardContent>
            </Card>
          ) : capacity.organizations.map(org => (
            <Card key={org.organization_id} className="border-slate-200">
              <CardContent className="p-4">
                <div className="flex justify-between items-start cursor-pointer" onClick={() => setExpandedOrg(expandedOrg === org.organization_id ? null : org.organization_id)}>
                  <div>
                    <h3 className="font-semibold text-sm text-slate-900 flex items-center gap-2">
                      <Building2 className="w-4 h-4 text-indigo-600" />
                      {org.organization_name}
                    </h3>
                    <div className="flex gap-4 mt-1 text-xs text-slate-500">
                      <span>{org.clinician_count} clinicians</span>
                      <span className="text-green-600">{org.accepting_count} accepting</span>
                      <span>{org.utilization_pct}% utilized</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-24 bg-gray-200 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full ${org.utilization_pct > 80 ? 'bg-red-500' : org.utilization_pct > 60 ? 'bg-amber-500' : 'bg-green-500'}`}
                        style={{ width: `${Math.min(org.utilization_pct, 100)}%` }}
                      />
                    </div>
                    {expandedOrg === org.organization_id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </div>
                </div>
                {expandedOrg === org.organization_id && (
                  <div className="mt-3 pt-3 border-t">
                    <div className="flex flex-wrap gap-1">
                      {(org.service_counties || []).map(c => (
                        <Badge key={c} variant="outline" className="text-xs"><MapPin className="w-2.5 h-2.5 mr-0.5" />{c}</Badge>
                      ))}
                    </div>
                    <div className="mt-2 text-xs text-slate-500">
                      Caseload: {org.current_caseload} / {org.max_capacity}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Cross-Org Referrals */}
      {view === 'cross-org' && (
        <div className="space-y-3">
          {crossOrgReferrals.length === 0 ? (
            <Card className="border-slate-200">
              <CardContent className="p-8 text-center">
                <Lock className="w-8 h-8 mx-auto text-slate-300 mb-3" />
                <p className="text-sm text-slate-500">No cross-org referrals yet.</p>
                <p className="text-xs text-slate-400 mt-1">Create a de-identified referral to route across the network.</p>
                <Button size="sm" className="mt-3 text-xs bg-indigo-600" onClick={() => setView('new-referral')}>
                  <Send className="w-3 h-3 mr-1" /> Create Referral
                </Button>
              </CardContent>
            </Card>
          ) : crossOrgReferrals.map(ref => (
            <Card key={ref.referral_id} className="border-slate-200" data-testid={`cross-org-ref-${ref.referral_id}`}>
              <CardContent className="p-4">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <Badge className={`text-xs ${statusColor(ref.status)}`}>{ref.status.replace(/_/g, ' ')}</Badge>
                      <Badge variant="outline" className="text-xs">
                        <Lock className="w-2.5 h-2.5 mr-0.5" />
                        {ref.phi_status === 'released' ? 'PHI Released' : 'De-identified'}
                      </Badge>
                      {ref.urgency === 'urgent' && <Badge className="bg-red-100 text-red-800 text-xs">Urgent</Badge>}
                      {ref.urgency === 'emergency' && <Badge className="bg-red-600 text-white text-xs">Emergency</Badge>}
                    </div>
                    <div className="text-sm font-medium text-slate-900">
                      {ref.service_type || 'General'} — {ref.age_group || '?'} {ref.gender || ''}, {ref.county || '?'} County
                    </div>
                    <div className="text-xs text-slate-500 mt-1">
                      {ref.presenting_need && <span>{ref.presenting_need.substring(0, 80)}{ref.presenting_need.length > 80 ? '...' : ''}</span>}
                    </div>
                    <div className="flex gap-2 mt-1.5 text-xs text-slate-400">
                      {ref.source_org_name && <span>From: {ref.source_org_name}</span>}
                      {ref.target_org_name && <span className="text-indigo-600 flex items-center gap-0.5"><ArrowRight className="w-3 h-3" /> {ref.target_org_name}</span>}
                      {ref.accepted_org_name && <span className="text-green-600 flex items-center gap-0.5"><CheckCircle className="w-3 h-3" /> {ref.accepted_org_name}</span>}
                    </div>
                    {/* AI match summary */}
                    {ref.ai_match_results && (
                      <div className="mt-2 p-2 rounded bg-violet-50 border border-violet-200 text-xs">
                        <div className="flex items-center gap-1 text-violet-700 font-medium mb-1">
                          <Sparkles className="w-3 h-3" /> Sage AI Match
                        </div>
                        <p className="text-violet-600">{ref.ai_match_results.summary?.substring(0, 120)}</p>
                        {ref.ai_match_results.matches?.length > 0 && (
                          <div className="mt-1 text-violet-500">
                            Top: {ref.ai_match_results.matches[0].clinician_name} at {ref.ai_match_results.matches[0].organization_name} ({ref.ai_match_results.matches[0].total_score}/100)
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col gap-1.5 ml-3">
                    {['intake', 'matching'].includes(ref.status) && (
                      <Button size="sm" variant="outline" className="text-xs h-7"
                        onClick={() => runAiMatch(ref.referral_id)}
                        disabled={matchingRefId === ref.referral_id}
                        data-testid={`ai-match-${ref.referral_id}`}>
                        {matchingRefId === ref.referral_id
                          ? <><RefreshCw className="w-3 h-3 mr-1 animate-spin" /> Matching</>
                          : <><Brain className="w-3 h-3 mr-1" /> AI Match</>}
                      </Button>
                    )}
                    {ref.status === 'pending_acceptance' && (
                      <>
                        <Button size="sm" className="text-xs h-7 bg-green-600 hover:bg-green-700"
                          onClick={() => acceptReferral(ref.referral_id)}
                          data-testid={`accept-ref-${ref.referral_id}`}>
                          <CheckCircle className="w-3 h-3 mr-1" /> Accept
                        </Button>
                        <Button size="sm" variant="outline" className="text-xs h-7 text-red-600 border-red-200"
                          onClick={() => setDecliningRefId(ref.referral_id)}
                          data-testid={`decline-ref-${ref.referral_id}`}>
                          <XCircle className="w-3 h-3 mr-1" /> Decline
                        </Button>
                      </>
                    )}
                  </div>
                </div>
                {/* Decline reason input */}
                {decliningRefId === ref.referral_id && (
                  <div className="mt-3 pt-3 border-t flex gap-2">
                    <input type="text" placeholder="Reason for declining..." value={declineReason}
                      onChange={e => setDeclineReason(e.target.value)}
                      className="flex-1 px-3 py-1.5 text-xs border rounded-md" />
                    <Button size="sm" className="text-xs h-7 bg-red-600" onClick={() => declineReferral(ref.referral_id)}>
                      Confirm Decline
                    </Button>
                    <Button size="sm" variant="outline" className="text-xs h-7" onClick={() => { setDecliningRefId(null); setDeclineReason(''); }}>
                      Cancel
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* AI Match Results Modal */}
      {aiMatchResult && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setAiMatchResult(null)}>
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full mx-4 max-h-[85vh] overflow-auto" onClick={e => e.stopPropagation()}>
            <div className="p-4 border-b sticky top-0 bg-white z-10">
              <div className="flex justify-between items-center">
                <h3 className="font-semibold text-sm flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-violet-600" /> Sage AI Cross-Org Match Results
                </h3>
                <button onClick={() => setAiMatchResult(null)} className="text-gray-400 hover:text-gray-600 text-lg">&times;</button>
              </div>
              {aiMatchResult.ai_summary && (
                <p className="text-xs text-violet-700 mt-2 bg-violet-50 rounded p-2">{aiMatchResult.ai_summary}</p>
              )}
              {aiMatchResult.compliance_note && (
                <p className="text-xs text-slate-500 mt-1 flex items-center gap-1">
                  <Shield className="w-3 h-3 text-green-600" /> {aiMatchResult.compliance_note}
                </p>
              )}
            </div>
            <div className="p-4 space-y-3">
              {(aiMatchResult.matches || []).map((match, i) => (
                <div key={i} className="border rounded-lg p-3">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="font-medium text-sm">#{match.rank} {match.clinician_name}</div>
                      <div className="flex gap-2 mt-0.5 text-xs">
                        <Badge variant="outline" className="text-xs">{match.organization_name}</Badge>
                        <span className="text-slate-500">Score: {match.total_score}/100</span>
                      </div>
                      {match.ai_rationale && (
                        <p className="text-xs text-violet-600 mt-1.5 bg-violet-50 rounded p-1.5">{match.ai_rationale}</p>
                      )}
                    </div>
                    <Button size="sm" className="text-xs h-7 bg-indigo-600 hover:bg-indigo-700 ml-3"
                      onClick={() => routeReferral(aiMatchResult.referralId, match.organization_id, match.clinician_id)}
                      data-testid={`route-to-${match.clinician_id}`}>
                      <ArrowRight className="w-3 h-3 mr-1" /> Route
                    </Button>
                  </div>
                </div>
              ))}
              {(!aiMatchResult.matches || aiMatchResult.matches.length === 0) && (
                <p className="text-sm text-slate-500 text-center py-6">No matches found across the network</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* New De-Identified Referral Form */}
      {view === 'new-referral' && (
        <Card className="border-slate-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <Shield className="w-4 h-4 text-green-600" /> Create De-Identified Cross-Org Referral
            </CardTitle>
            <p className="text-xs text-slate-500 mt-1">
              Clinical needs are shared across the network. PHI (name, DOB, Medicaid ID) stays locked until the receiving org accepts.
            </p>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* De-identified clinical profile */}
            <div>
              <h4 className="text-xs font-semibold text-slate-700 mb-2 flex items-center gap-1"><Globe className="w-3 h-3" /> Shared with Network (De-identified)</h4>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                <select value={newRef.age_group} onChange={e => setNewRef({...newRef, age_group: e.target.value})}
                  className="px-3 py-1.5 text-sm border rounded-md" data-testid="ref-age-group">
                  <option value="">Age Group</option>
                  <option value="0-5">0-5</option><option value="6-12">6-12</option>
                  <option value="13-17">13-17</option><option value="18-25">18-25</option>
                  <option value="26-40">26-40</option><option value="41-64">41-64</option>
                  <option value="65+">65+</option>
                </select>
                <select value={newRef.gender} onChange={e => setNewRef({...newRef, gender: e.target.value})}
                  className="px-3 py-1.5 text-sm border rounded-md">
                  <option value="">Gender</option>
                  <option value="male">Male</option><option value="female">Female</option>
                  <option value="non-binary">Non-binary</option><option value="other">Other</option>
                </select>
                <input type="text" placeholder="County *" value={newRef.county}
                  onChange={e => setNewRef({...newRef, county: e.target.value})}
                  className="px-3 py-1.5 text-sm border rounded-md" data-testid="ref-county" />
                <input type="text" placeholder="Service Type *" value={newRef.service_type}
                  onChange={e => setNewRef({...newRef, service_type: e.target.value})}
                  className="px-3 py-1.5 text-sm border rounded-md" data-testid="ref-service-type" />
                <select value={newRef.urgency} onChange={e => setNewRef({...newRef, urgency: e.target.value})}
                  className="px-3 py-1.5 text-sm border rounded-md">
                  <option value="routine">Routine</option><option value="urgent">Urgent</option>
                  <option value="emergency">Emergency</option>
                </select>
                <select value={newRef.risk_level} onChange={e => setNewRef({...newRef, risk_level: e.target.value})}
                  className="px-3 py-1.5 text-sm border rounded-md">
                  <option value="low">Low Risk</option><option value="moderate">Moderate</option>
                  <option value="high">High Risk</option>
                </select>
                <input type="text" placeholder="Diagnosis codes (comma-separated)" value={newRef.diagnosis_codes}
                  onChange={e => setNewRef({...newRef, diagnosis_codes: e.target.value})}
                  className="px-3 py-1.5 text-sm border rounded-md col-span-2 md:col-span-3" />
              </div>
              <textarea placeholder="Presenting need / clinical summary (de-identified)..." value={newRef.presenting_need}
                onChange={e => setNewRef({...newRef, presenting_need: e.target.value})}
                className="w-full px-3 py-1.5 text-sm border rounded-md h-16 mt-2" data-testid="ref-presenting-need" />
              <div className="grid grid-cols-3 gap-3 mt-2">
                <select value={newRef.language_preference} onChange={e => setNewRef({...newRef, language_preference: e.target.value})}
                  className="px-3 py-1.5 text-sm border rounded-md">
                  <option value="English">English</option><option value="Spanish">Spanish</option>
                  <option value="Other">Other</option>
                </select>
                <select value={newRef.preferred_modality} onChange={e => setNewRef({...newRef, preferred_modality: e.target.value})}
                  className="px-3 py-1.5 text-sm border rounded-md">
                  <option value="any">Any Modality</option><option value="in-person">In-Person</option>
                  <option value="telehealth">Telehealth</option><option value="hybrid">Hybrid</option>
                </select>
                <select value={newRef.payer_type} onChange={e => setNewRef({...newRef, payer_type: e.target.value})}
                  className="px-3 py-1.5 text-sm border rounded-md">
                  <option value="medicaid">Medicaid</option><option value="commercial">Commercial</option>
                  <option value="self-pay">Self-Pay</option><option value="other">Other</option>
                </select>
              </div>
            </div>

            {/* PHI Vault — secured */}
            <div className="border-t pt-4">
              <h4 className="text-xs font-semibold text-slate-700 mb-2 flex items-center gap-1">
                <Lock className="w-3 h-3 text-red-500" /> PHI Vault (Locked until org accepts)
              </h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <input type="text" placeholder="First Name" value={newRef.client_first_name}
                  onChange={e => setNewRef({...newRef, client_first_name: e.target.value})}
                  className="px-3 py-1.5 text-sm border rounded-md border-red-200 bg-red-50/30" />
                <input type="text" placeholder="Last Name" value={newRef.client_last_name}
                  onChange={e => setNewRef({...newRef, client_last_name: e.target.value})}
                  className="px-3 py-1.5 text-sm border rounded-md border-red-200 bg-red-50/30" />
                <input type="date" placeholder="DOB" value={newRef.client_dob}
                  onChange={e => setNewRef({...newRef, client_dob: e.target.value})}
                  className="px-3 py-1.5 text-sm border rounded-md border-red-200 bg-red-50/30" />
                <input type="text" placeholder="Medicaid ID" value={newRef.medicaid_id}
                  onChange={e => setNewRef({...newRef, medicaid_id: e.target.value})}
                  className="px-3 py-1.5 text-sm border rounded-md border-red-200 bg-red-50/30" />
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button size="sm" variant="outline" onClick={() => setView('overview')} className="text-xs">Cancel</Button>
              <Button size="sm" onClick={createDeidentifiedReferral}
                disabled={loading || !newRef.county || !newRef.service_type}
                className="text-xs bg-indigo-600 hover:bg-indigo-700"
                data-testid="create-deidentified-referral-btn">
                <Shield className="w-3 h-3 mr-1" /> Create De-Identified Referral
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Search clinicians across network */}
      {view === 'search' && (
        <Card className="border-slate-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold">Search Clinicians Across Network</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex gap-2">
              <input type="text" placeholder="County..." value={searchCounty}
                onChange={e => setSearchCounty(e.target.value)}
                className="flex-1 px-3 py-1.5 text-sm border rounded-md" data-testid="platform-search-county" />
              <input type="text" placeholder="Specialization..." value={searchSpec}
                onChange={e => setSearchSpec(e.target.value)}
                className="flex-1 px-3 py-1.5 text-sm border rounded-md" data-testid="platform-search-spec" />
              <Button size="sm" onClick={searchClinicians} disabled={loading} className="text-xs">
                <Search className="w-3 h-3 mr-1" /> Search
              </Button>
            </div>
            <div className="space-y-2">
              {clinicians.map(c => (
                <div key={c.id} className="border rounded-lg p-3 text-sm flex justify-between items-center">
                  <div>
                    <div className="font-medium">{c.full_name}</div>
                    <div className="text-xs text-slate-500 flex gap-2">
                      <Badge variant="outline" className="text-xs">{c.organization_name}</Badge>
                      <span>{c.role}</span>
                    </div>
                  </div>
                  <Badge className={c.nexus_profile?.accepting_new ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}>
                    {c.nexus_profile?.accepting_new ? 'Accepting' : 'Full'}
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Legacy Referrals */}
      {view === 'referrals' && (
        <div className="space-y-3">
          {referrals.length === 0 ? (
            <Card className="border-slate-200">
              <CardContent className="p-8 text-center text-sm text-slate-500">
                No legacy platform referrals. Use "Cross-Org Referrals" for HIPAA-compliant routing.
              </CardContent>
            </Card>
          ) : referrals.map(ref => (
            <Card key={ref.referral_id} className="border-slate-200">
              <CardContent className="p-4">
                <div className="flex justify-between items-start">
                  <div>
                    <h4 className="font-semibold text-sm">{ref.client_first_name} {ref.client_last_name}</h4>
                    <div className="flex gap-2 mt-1 flex-wrap">
                      <Badge className={`text-xs ${statusColor(ref.status)}`}>{ref.status}</Badge>
                      {ref.county && <Badge variant="outline" className="text-xs"><MapPin className="w-2.5 h-2.5 mr-0.5" />{ref.county}</Badge>}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
        </>
      )}
    </div>
  );
};

export default NexusPlatformView;
